import { useNavigate } from "@tanstack/react-router";

import { useCurrentWorkspace } from "@/api/resources/workspaces/hooks";
import type { CaseNavigationType } from "@/components/Case/types";
import type { CustomInventoryNavigationType } from "@/components/CustomInventory/types";
import type { DeviceInsightsNavigationType } from "@/components/DeviceInsights/types";
import type { MaintenanceNavigationType } from "@/components/Maintenance/types";
import type { WorkspaceTagsNavigationType } from "@/components/WorkspaceTags/types";

export const useNavigation = () => {
  const navigate = useNavigate();
  const { data: currentWorkspace } = useCurrentWorkspace();

  const goToRedirect = (redirect?: string) => {
    void navigate({ to: redirect });
  };

  const goToLogin = () => {
    void navigate({ to: "/login" });
  };

  const goToLogout = () => {
    void navigate({ to: "/logout" });
  };

  const goToRegister = () => {
    void navigate({ to: "/register" });
  };

  const goToForgotPassword = () => {
    void navigate({ to: "/account/password/request" });
  };

  const goToSetPassword = () => {
    void navigate({ to: "/account/password/set" });
  };
  const goToResetPassword = () => {
    void navigate({ to: "/account/password/reset" });
  };

  const goToAcademy = () => {
    void navigate({ to: "/academy" });
  };

  const goToAccountSettings = () => {
    void navigate({ to: "/account/profile" });
  };

  const goToEndOfTrial = () => {
    void navigate({
      to: "/end-of-trial",
    });
  };

  const goToOnboarding = () => {
    void navigate({ to: "/welcome" });
  };

  const goToDashboard = () => {
    void navigate({ to: "/" });
  };

  const goToWorkspace = (workspace_id: string) => {
    void navigate({
      to: "/workspaces/$workspace_id",
      params: { workspace_id: workspace_id },
    });
  };

  const goToWorkspaceSettings = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/settings/details",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToWorkspaceProfile = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/settings/profile",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToWorkspaceUsers = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/settings/access",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToWorkspaceUser = (user_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/settings/access/users/$user_id",
        params: { workspace_id: currentWorkspace.id, user_id: user_id },
      });
    }
  };

  const goToDeployments = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/deployments",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToDeployment = (deployment_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/deployments/$deployment_id",
        params: { workspace_id: currentWorkspace.id, deployment_id: deployment_id },
      });
    }
  };

  const goToDeploymentUser = (deployment_id: string, user_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/deployments/$deployment_id/users/$user_id",
        params: { workspace_id: currentWorkspace.id, deployment_id: deployment_id, user_id: user_id },
      });
    }
  };

  const goToDevices = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/devices",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToDevice = (device_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/devices/$device_id",
        params: { workspace_id: currentWorkspace.id, device_id: device_id },
      });
    }
  };

  const goToDevicePart = (device_id: string, part_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/devices/$device_id/parts/$part_id",
        params: { workspace_id: currentWorkspace.id, device_id: device_id, part_id: part_id },
      });
    }
  };

  const goToCases = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/cases",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToCase = (case_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/cases/$case_id",
        params: { workspace_id: currentWorkspace.id, case_id: case_id },
      });
    }
  };

  const goToChecklistTemplates = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/checklist-templates",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToChecklistTemplate = (checklist_template_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/checklist-templates/$checklist_template_id",
        params: { workspace_id: currentWorkspace.id, checklist_template_id: checklist_template_id },
      });
    }
  };

  const goToMaintenances = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/maintenances",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToMaintenance = (maintenance_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/maintenances/$maintenance_id",
        params: { workspace_id: currentWorkspace.id, maintenance_id: maintenance_id },
      });
    }
  };

  const goToMaintenanceDevice = (maintenance_id: string, device_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/maintenances/$maintenance_id/devices/$device_id",
        params: { workspace_id: currentWorkspace.id, maintenance_id: maintenance_id, device_id: device_id },
      });
    }
  };

  const goToMaintenanceStrategies = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/maintenance-strategies",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToMaintenanceStrategy = (maintenance_strategy_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/maintenance-strategies/$maintenance_strategy_id",
        params: { workspace_id: currentWorkspace.id, maintenance_strategy_id: maintenance_strategy_id },
      });
    }
  };

  const goToInventoryParts = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/inventory/parts",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToInventoryPart = (part_id: string) => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/inventory/parts/$part_id",
        params: { workspace_id: currentWorkspace.id, part_id: part_id },
      });
    }
  };

  const goToInventoryPartsHistories = () => {
    if (currentWorkspace?.id) {
      void navigate({
        to: "/workspaces/$workspace_id/inventory/activities",
        params: { workspace_id: currentWorkspace.id },
      });
    }
  };

  const goToCustomInventoryType = (value?: CustomInventoryNavigationType | null) => {
    if (currentWorkspace?.id && value) {
      void navigate({
        to: "/workspaces/$workspace_id/settings/custom-inventory/$type",
        params: { workspace_id: currentWorkspace.id, type: value },
      });
    }
  };

  const goToDeviceInsightsType = (value?: DeviceInsightsNavigationType | null) => {
    if (currentWorkspace?.id && value) {
      void navigate({
        to: "/workspaces/$workspace_id/device-insights/$type",
        params: { workspace_id: currentWorkspace.id, type: value },
      });
    }
  };

  const goToCaseType = (value?: CaseNavigationType | null) => {
    if (currentWorkspace?.id && value) {
      void navigate({
        to: "/workspaces/$workspace_id/cases/type/$type",
        params: { workspace_id: currentWorkspace.id, type: value },
      });
    }
  };

  const goToMaintenanceType = (value?: MaintenanceNavigationType | null) => {
    if (currentWorkspace?.id && value) {
      void navigate({
        to: "/workspaces/$workspace_id/maintenances/type/$type",
        params: { workspace_id: currentWorkspace.id, type: value },
      });
    }
  };

  const goToWorkspaceTagsType = (value?: WorkspaceTagsNavigationType | null) => {
    if (currentWorkspace && value) {
      void navigate({
        to: "/workspaces/$workspace_id/settings/tags/$type",
        params: { workspace_id: currentWorkspace.id, type: value },
      });
    }
  };

  return {
    goToRedirect,
    goToLogin,
    goToLogout,
    goToRegister,
    goToForgotPassword,
    goToSetPassword,
    goToResetPassword,
    goToAcademy,
    goToAccountSettings,
    goToEndOfTrial,
    goToOnboarding,
    goToDashboard,
    goToWorkspace,
    goToWorkspaceSettings,
    goToWorkspaceProfile,
    goToWorkspaceUsers,
    goToWorkspaceUser,
    goToDeployments,
    goToDeployment,
    goToDeploymentUser,
    goToDevices,
    goToDevice,
    goToDevicePart,
    goToCases,
    goToCase,
    goToChecklistTemplates,
    goToChecklistTemplate,
    goToMaintenances,
    goToMaintenance,
    goToMaintenanceDevice,
    goToMaintenanceStrategies,
    goToMaintenanceStrategy,
    goToInventoryParts,
    goToInventoryPart,
    goToInventoryPartsHistories,
    goToCustomInventoryType,
    goToDeviceInsightsType,
    goToCaseType,
    goToMaintenanceType,
    goToWorkspaceTagsType,
  };
};
